import { useEffect } from 'react';

interface DocumentMetaOptions {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: 'article' | 'website' | 'profile';
  twitterCard?: 'summary' | 'summary_large_image';
}

/**
 * Hook for setting document meta tags client-side.
 *
 * Note: For social sharing previews (Twitter, Facebook, Slack, etc.),
 * crawlers don't execute JavaScript, so these tags won't be seen.
 * Server-side rendering or pre-rendering is required for proper OG tags.
 *
 * This hook is useful for:
 * - Setting the document title for browser tabs
 * - SEO for crawlers that do execute JS (Googlebot)
 * - In-app navigation state
 */
export function useDocumentMeta(options: DocumentMetaOptions) {
  useEffect(() => {
    const originalTitle = document.title;
    const addedMetas: HTMLMetaElement[] = [];

    // Set document title
    if (options.title) {
      document.title = options.title;
    }

    // Helper to set or update a meta tag
    function setMeta(name: string, content: string, isProperty = false) {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
        addedMetas.push(meta);
      }
      meta.content = content;
    }

    // Standard meta tags
    if (options.description) {
      setMeta('description', options.description);
    }

    // Open Graph tags
    if (options.ogTitle || options.title) {
      setMeta('og:title', options.ogTitle || options.title!, true);
    }
    if (options.ogDescription || options.description) {
      setMeta('og:description', options.ogDescription || options.description!, true);
    }
    if (options.ogImage) {
      setMeta('og:image', options.ogImage, true);
    }
    if (options.ogUrl) {
      setMeta('og:url', options.ogUrl, true);
    }
    if (options.ogType) {
      setMeta('og:type', options.ogType, true);
    }

    // Twitter Card tags
    if (options.twitterCard) {
      setMeta('twitter:card', options.twitterCard);
    }
    if (options.ogTitle || options.title) {
      setMeta('twitter:title', options.ogTitle || options.title!);
    }
    if (options.ogDescription || options.description) {
      setMeta('twitter:description', options.ogDescription || options.description!);
    }
    if (options.ogImage) {
      setMeta('twitter:image', options.ogImage);
    }

    // Cleanup on unmount
    return () => {
      document.title = originalTitle;
      addedMetas.forEach(meta => meta.remove());
    };
  }, [
    options.title,
    options.description,
    options.ogTitle,
    options.ogDescription,
    options.ogImage,
    options.ogUrl,
    options.ogType,
    options.twitterCard
  ]);
}

/**
 * Generate a share URL for different content types
 */
export function getShareUrl(type: 'article' | 'collection' | 'extract', token: string): string {
  const prefix = type === 'article' ? 's' : type === 'collection' ? 'c' : 'e';
  return `${window.location.origin}/${prefix}/${token}`;
}

/**
 * Generate default OG image URL (placeholder - implement with image generation service)
 */
export function getOgImageUrl(_params: {
  title: string;
  type: 'article' | 'collection' | 'extract';
  author?: string;
}): string {
  // This would point to an image generation service
  // For now, return a default image
  return `${window.location.origin}/og-default.png`;
}
